import { gql } from "generated-graphql";

/**
 * Shared row shape for the episodic events ledger. `submissions` is the
 * EPA-notification filing history (newest first); `containers` is the set
 * currently accumulating under the event's 60-day window.
 */
export const EPISODIC_EVENT_ITEM_FRAGMENT = gql(`
  fragment EpisodicEventItem on WasteEpisodicEvent {
    id
    facilityId
    type
    status
    reason
    startDate
    endDate
    accumulationDeadline
    manuallyReopenedAt
    notices {
      id
      kind
      noticeDate
      method
      note
      document {
        id
        title
        fileExtension
      }
    }
    submissions {
      id
      title
      status
      dueDate
      submittedAt
    }
    containers {
      id
      containerLabel
      status
      amount
      amountUnit
      archivedAt
      accumulationArea {
        id
        name
        type
      }
      wasteStream {
        id
        name
      }
    }
  }
`);

/** Facility-scoped year ledger — filterable by status, paginated. */
export const FACILITY_EPISODIC_EVENTS_QUERY = gql(`
  query FacilityEpisodicEvents(
    $facilityId: ID!
    $status: WasteEpisodicEventStatus
    $page: Int
    $pageSize: Int
  ) {
    facility(id: $facilityId) {
      id
      name
      generationDrivenStatus
      episodicEvents(status: $status, page: $page, pageSize: $pageSize) {
        items {
          ...EpisodicEventItem
        }
        count
      }
    }
  }
`);

export const EPISODIC_EVENT_VALIDATOR_QUERY = gql(`
  query EpisodicEventInputValidator($input: EpisodicEventInput!, $currentEventId: ID) {
    episodicEventInputValidator(input: $input, currentEventId: $currentEventId) {
      ...issue
    }
  }
`);

export const CREATE_EPISODIC_EVENT_MUTATION = gql(`
  mutation CreateEpisodicEvent($input: CreateEpisodicEventInput!) {
    createEpisodicEvent(input: $input) {
      ...EpisodicEventItem
    }
  }
`);

export const UPDATE_EPISODIC_EVENT_MUTATION = gql(`
  mutation UpdateEpisodicEvent($input: UpdateEpisodicEventInput!) {
    updateEpisodicEvent(input: $input)
  }
`);

export const FINALIZE_EPISODIC_EVENT_MUTATION = gql(`
  mutation FinalizeEpisodicEvent($id: ID!) {
    finalizeEpisodicEvent(id: $id)
  }
`);

export const REOPEN_EPISODIC_EVENT_MUTATION = gql(`
  mutation ReopenEpisodicEvent($id: ID!) {
    reopenEpisodicEvent(id: $id)
  }
`);

export const DELETE_EPISODIC_EVENT_MUTATION = gql(`
  mutation DeleteEpisodicEvent($id: ID!) {
    deleteEpisodicEvent(id: $id)
  }
`);

export const SET_CONTAINER_EPISODIC_EVENT_MUTATION = gql(`
  mutation SetContainerEpisodicEvent($containerId: ID!, $episodicEventId: ID) {
    setContainerEpisodicEvent(containerId: $containerId, episodicEventId: $episodicEventId)
  }
`);

export const CREATE_EPISODIC_EVENT_SUBMISSION_MUTATION = gql(`
  mutation CreateEpisodicEventSubmission($eventId: ID!) {
    createEpisodicEventSubmission(eventId: $eventId)
  }
`);

export const UPSERT_EPISODIC_EVENT_NOTICE_MUTATION = gql(`
  mutation UpsertEpisodicEventNotice($input: UpsertEpisodicEventNoticeInput!) {
    upsertEpisodicEventNotice(input: $input) {
      id
      kind
      noticeDate
      method
      note
      document {
        id
        title
        fileExtension
      }
    }
  }
`);

export const DELETE_EPISODIC_EVENT_NOTICE_MUTATION = gql(`
  mutation DeleteEpisodicEventNotice($eventId: ID!, $kind: WasteEpisodicEventNoticeKind!) {
    deleteEpisodicEventNotice(eventId: $eventId, kind: $kind)
  }
`);
